import React, { useState, useEffect } from 'react';

/**
 * AlertsSettings - Configure notification settings and system alert thresholds
 *
 * Props:
 *   - configs: MonitoringControl_configs object
 *   - onSave: function to save updated configs
 *   - saveStatus: 'success' | 'error' | ''
 */
const AlertsSettings = ({ configs, onSave, saveStatus }) => {
  const [localConfigs, setLocalConfigs] = useState(configs);

  // Sync local state when props change
  useEffect(() => {
    setLocalConfigs(configs);
  }, [configs]);

  // Extract settings with defaults
  const systemAlerts = localConfigs?.system_alerts || {};
  const notificationConfig = localConfigs?.notification_config || {};

  // Offline alert settings
  const offlineConfig = systemAlerts?.offline || {};
  const offlineEnabled = offlineConfig?.enabled !== false;
  const offlineThresholdMinutes = offlineConfig?.threshold_minutes || 60;

  // Network outage settings
  const networkConfig = systemAlerts?.network_outage || {};
  const networkEnabled = networkConfig?.enabled !== false;

  // Notification settings
  const notificationsEnabled = notificationConfig?.enabled !== false;
  const defaultEmail = notificationConfig?.default_email || '';

  // Quiet hours settings
  const quietHours = notificationConfig?.quiet_hours || {};
  const quietHoursEnabled = quietHours?.enabled || false;
  const quietStart = quietHours?.start || '22:00';
  const quietEnd = quietHours?.end || '07:00';

  // Handle toggle changes
  const handleToggle = (path, value) => {
    const pathParts = path.split('.');
    setLocalConfigs(prev => {
      const updated = { ...prev };

      // Navigate to the correct nested location
      let current = updated;
      for (let i = 0; i < pathParts.length - 1; i++) {
        if (!current[pathParts[i]]) {
          current[pathParts[i]] = {};
        }
        current[pathParts[i]] = { ...current[pathParts[i]] };
        current = current[pathParts[i]];
      }

      current[pathParts[pathParts.length - 1]] = value;
      return updated;
    });
  };

  // Handle input changes
  const handleInputChange = (path, value) => {
    handleToggle(path, value);
  };

  // Handle save
  const handleSave = () => {
    onSave(localConfigs);
  };

  return (
    <div className="alerts-settings">
      <h3>Alert Settings</h3>

      {saveStatus === 'success' && <p className="save-status success">Settings saved successfully</p>}
      {saveStatus === 'error' && <p className="save-status error">Error saving settings</p>}

      {/* System Alerts Section */}
      <div className="settings-section">
        <h4>System Alerts</h4>

        <div className="settings-group">
          <div className="setting-row">
            <label className="setting-label">
              <input
                type="checkbox"
                checked={offlineEnabled}
                onChange={(e) => handleToggle('system_alerts.offline.enabled', e.target.checked)}
              />
              Offline Detection
            </label>
            <span className="setting-description">
              Alert when devices stop reporting data
            </span>
          </div>

          {offlineEnabled && (
            <div className="setting-row indented">
              <label className="setting-label">
                Threshold (minutes):
                <input
                  type="number"
                  min="15"
                  max="1440"
                  value={offlineThresholdMinutes}
                  onChange={(e) => handleInputChange(
                    'system_alerts.offline.threshold_minutes',
                    Number(e.target.value)
                  )}
                />
              </label>
              <span className="setting-description">
                Alert after device is offline for this duration
              </span>
            </div>
          )}
        </div>

        <div className="settings-group">
          <div className="setting-row">
            <label className="setting-label">
              <input
                type="checkbox"
                checked={networkEnabled}
                onChange={(e) => handleToggle('system_alerts.network_outage.enabled', e.target.checked)}
              />
              Network Outage Detection
            </label>
            <span className="setting-description">
              Alert when multiple devices go offline simultaneously (possible network issue)
            </span>
          </div>
        </div>
      </div>

      {/* Notification Settings Section */}
      <div className="settings-section">
        <h4>Notifications</h4>

        <div className="settings-group">
          <div className="setting-row">
            <label className="setting-label">
              <input
                type="checkbox"
                checked={notificationsEnabled}
                onChange={(e) => handleToggle('notification_config.enabled', e.target.checked)}
              />
              Email Notifications
            </label>
            <span className="setting-description">
              Receive email alerts when alerts are triggered or resolved
            </span>
          </div>

          {notificationsEnabled && (
            <>
              <div className="setting-row indented">
                <label className="setting-label">
                  Recipient Email:
                  <input
                    type="email"
                    value={defaultEmail}
                    onChange={(e) => handleInputChange('notification_config.default_email', e.target.value)}
                    placeholder="email@example.com"
                  />
                </label>
                <span className="setting-description">
                  Leave blank to use account email
                </span>
              </div>

              <div className="setting-row">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={quietHoursEnabled}
                    onChange={(e) => handleToggle('notification_config.quiet_hours.enabled', e.target.checked)}
                  />
                  Quiet Hours
                </label>
                <span className="setting-description">
                  Suppress non-critical notifications during specified hours
                </span>
              </div>

              {quietHoursEnabled && (
                <div className="setting-row indented">
                  <label className="setting-label">
                    From:
                    <input
                      type="time"
                      value={quietStart}
                      onChange={(e) => handleInputChange('notification_config.quiet_hours.start', e.target.value)}
                    />
                  </label>
                  <label className="setting-label">
                    To:
                    <input
                      type="time"
                      value={quietEnd}
                      onChange={(e) => handleInputChange('notification_config.quiet_hours.end', e.target.value)}
                    />
                  </label>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="settings-actions">
        <button onClick={handleSave} className="save-button">
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default AlertsSettings;
